import { useState } from "react";
import { Button, Icon, Modal } from "semantic-ui-react";
import { Document, Page, pdfjs } from "react-pdf";
import { FormattedMessage, useIntl } from "react-intl";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import styles from "./PurchaseHistory.module.scss";
import downloadStyles from "./DownloadPatterns.module.scss";
import api from "../../utils/api";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url
).toString();

interface PatternFile {
    name: string;
    url: string;
}

interface DownloadUrlResponse {
    files: PatternFile[];
}

const DownloadPatterns = ({ patternName, patternId }: { patternName: string; patternId: string }) => {
    const [downloadData, setDownloadData] = useState<DownloadUrlResponse | null>(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [previewFile, setPreviewFile] = useState<PatternFile | null>(null);
    const [numPages, setNumPages] = useState<number>(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [downloading, setDownloading] = useState(false);
    const intl = useIntl();

    const handleOpenModal = async () => {
        setLoading(true);
        try {
            const res = await api.get(`/patterns/${patternId}/download`);
            setDownloadData(res.data);
            setModalOpen(true);
        } catch (error) {
            console.error("Error fetching download URL:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setModalOpen(false);
        setPreviewFile(null);
        setNumPages(0);
        setCurrentPage(1);
    };

    const handleSelectFile = (file: PatternFile) => {
        setPreviewFile(file);
        setCurrentPage(1);
        setNumPages(0);
    };

    const handleDownload = async (file: PatternFile) => {
        setDownloading(true);
        try {
            const response = await fetch(file.url);
            const blob = await response.blob();
            const blobUrl = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = blobUrl;
            a.download = file.name;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(blobUrl);
        } catch (err) {
            console.error("Download failed:", err);
        } finally {
            setDownloading(false);
        }
    };

    const formatFileName = (name: string) =>
        name.replace(/-[a-f0-9]+\.pdf$/, ".pdf").replace(/-/g, " ");

    return (
        <>
            <Button
                icon
                size="mini"
                className={styles.downloadOutlineBtn}
                onClick={handleOpenModal}
                title={intl.formatMessage({
                  id: "order.history.downloadPattern.title",
                  defaultMessage: "Download Pattern",
                })}
                loading={loading}
                disabled={loading}
            >
                {!loading && <Icon name="download" />}
            </Button>

            <Modal
                open={modalOpen}
                onClose={handleClose}
                size="large"
                className={downloadStyles.modal}
            >
                {/* Header */}
                <div className={downloadStyles.header}>
                    <div className={downloadStyles.headerLeft}>
                        <Icon name="file pdf outline" className={downloadStyles.headerIcon} />
                        <span className={downloadStyles.headerTitle}>{patternName}</span>
                    </div>
                    <button onClick={handleClose} title={intl.formatMessage({
                      id: "order.history.downloadPattern.close",
                      defaultMessage: "Close",
                    })} className={downloadStyles.closeBtn}>
                        <Icon name="times" className={downloadStyles.closeBtnIcon} />
                    </button>
                </div>

                <Modal.Content className={downloadStyles.content}>
                    {/* Left panel — file list */}
                    <div className={downloadStyles.fileList}>
                        <p className={downloadStyles.fileListLabel}>
                            <FormattedMessage
                              id="order.history.downloadPattern.availableFiles"
                              defaultMessage="Available Files ({count})"
                              values={{ count: downloadData?.files?.length ?? 0 }}
                            />
                        </p>

                        {downloadData?.files?.map((file, idx) => {
                            const active = previewFile?.url === file.url;
                            return (
                                <div
                                    key={idx}
                                    onClick={() => handleSelectFile(file)}
                                    className={`${downloadStyles.fileItem} ${active ? downloadStyles.fileItemActive : ""}`}
                                >
                                    <Icon name="file pdf outline" className={downloadStyles.fileItemIcon} />
                                    <div>
                                        <div className={downloadStyles.fileItemName}>
                                            {formatFileName(file.name)}
                                        </div>
                                        <div className={downloadStyles.fileItemMeta}>
                                          <FormattedMessage
                                            id="order.history.downloadPattern.pdfDocument"
                                            defaultMessage="PDF Document"
                                          />
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Right panel — react-pdf viewer */}
                    <div className={downloadStyles.viewer}>
                        {previewFile ? (
                            <>
                                {/* Page controls */}
                                <div className={downloadStyles.pageControls}>
                                    <span className={downloadStyles.pageControlsFileName}>
                                        {formatFileName(previewFile.name)}
                                    </span>

                                    {numPages > 0 && (
                                        <div className={downloadStyles.pageNav}>
                                            <button
                                                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                                disabled={currentPage <= 1}
                                                className={downloadStyles.pageNavBtn}
                                            >
                                                <Icon name="chevron left" className={downloadStyles.pageNavIcon} />
                                            </button>
                                            <span className={downloadStyles.pageNavText}>
                                                {currentPage} / {numPages}
                                            </span>
                                            <button
                                                onClick={() => setCurrentPage(p => Math.min(numPages, p + 1))}
                                                disabled={currentPage >= numPages}
                                                className={downloadStyles.pageNavBtn}
                                            >
                                                <Icon name="chevron right" className={downloadStyles.pageNavIcon} />
                                            </button>
                                        </div>
                                    )}

                                    <Button
                                        size="small"
                                        onClick={() => handleDownload(previewFile)}
                                        loading={downloading}
                                        className={downloadStyles.downloadBtn}
                                    >
                                        <Icon name="download" />
                                        <FormattedMessage
                                          id="order.history.downloadPattern.download"
                                          defaultMessage="Download"
                                        />
                                    </Button>
                                </div>

                                {/* PDF render area */}
                                <div className={downloadStyles.pdfArea}>
                                    <Document
                                        file={previewFile.url}
                                        onLoadSuccess={({ numPages }: { numPages: number }) => setNumPages(numPages)}
                                        loading={
                                            <div className={downloadStyles.pdfLoading}>
                                                <Icon name="spinner" loading size="large" />
                                                <p>
                                                  <FormattedMessage
                                                    id="order.history.downloadPattern.loadingPdf"
                                                    defaultMessage="Loading PDF..."
                                                  />
                                                </p>
                                            </div>
                                        }
                                        error={
                                            <div className={downloadStyles.pdfError}>
                                                <Icon name="warning circle" size="large" />
                                                <p>
                                                  <FormattedMessage
                                                    id="order.history.downloadPattern.failedToLoadPdf"
                                                    defaultMessage="Failed to load PDF."
                                                  />
                                                </p>
                                            </div>
                                        }
                                    >
                                        <Page
                                            pageNumber={currentPage}
                                            width={580}
                                            renderTextLayer={true}
                                            renderAnnotationLayer={true}
                                        />
                                    </Document>
                                </div>
                            </>
                        ) : (
                            <div className={downloadStyles.emptyState}>
                                <p className={downloadStyles.emptyStateText}>
                                  <FormattedMessage
                                    id="order.history.downloadPattern.selectFilePreview"
                                    defaultMessage="Select a file to preview"
                                  />
                                </p>
                            </div>
                        )}
                    </div>
                </Modal.Content>

                {/* Footer — download all */}
                {(downloadData?.files?.length ?? 0) > 1 && (
                    <Modal.Actions className={downloadStyles.footer}>
                        <Button
                            onClick={() => downloadData?.files?.forEach(f => handleDownload(f))}
                            className={downloadStyles.downloadAllBtn}
                        >
                            <Icon name="download" />
                            <FormattedMessage
                              id="order.history.downloadPattern.downloadAll"
                              defaultMessage="Download All"
                            />
                        </Button>
                    </Modal.Actions>
                )}
            </Modal>
        </>
    );
};

export default DownloadPatterns;